import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { COURSES } from '@/data/courses.ts';
import QuestionBlock from '@/components/course/QuestionBlock';
import ConceptTag from '@/components/course/ConceptTag';
import { useGame } from '@/context/GameContext';
import { useAuth } from '@/context/AuthContext';
import { LoginModal } from '@/components';
import { cn } from '@/lib/utils';
import { Module, Question } from '@/types';
import SEO from '@/components/layout/SEO';
import { msUntilNextLife } from '@/lib/game/lives';

type Phase = 'reading' | 'quiz' | 'complete' | 'fail';

type CalloutTone = 'tip' | 'quiz';

const CALLOUT_PATTERNS: Array<{
    tone: CalloutTone;
    pattern: RegExp;
    icon: string;
    label: string;
    accentClass: string;
    badgeClass: string;
    bodyClass: string;
}> = [
    {
        tone: 'tip',
        pattern: /^🎯\s*TIP:\s*/i,
        icon: '◈',
        label: 'TACTICAL TIP',
        accentClass: 'border-[#00cffc]/30 bg-[#00cffc]/6',
        badgeClass: 'border-[#00cffc]/30 bg-[#00cffc]/10 text-[#00cffc]',
        bodyClass: 'text-[#dedede]',
    },
    {
        tone: 'quiz',
        pattern: /^🧠\s*QUIZ BRIEFING:\s*/i,
        icon: '◎',
        label: 'QUIZ BRIEFING',
        accentClass: 'border-[#8cacff]/30 bg-[#8cacff]/6',
        badgeClass: 'border-[#8cacff]/30 bg-[#8cacff]/10 text-[#8cacff]',
        bodyClass: 'text-[#c9d4ff]',
    },
];

function getSectionCallout(body?: string | null) {
    if (!body) return null;

    for (const callout of CALLOUT_PATTERNS) {
        if (callout.pattern.test(body)) {
            return {
                ...callout,
                content: body.replace(callout.pattern, '').trim(),
            };
        }
    }

    return null;
}

function formatMs(ms: number | null) {
    if (ms === null || ms <= 0) return '00:00:00';
    const totalSeconds = Math.floor(ms / 1000);
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    return [hours, minutes, seconds].map((value) => String(value).padStart(2, '0')).join(':');
}

function getSectionHeading(type: string, index: number) {
    if (type === 'analogy') return `[ ${String(index + 1).padStart(2, '0')} ] Intelligence Overview`;
    if (type === 'code') return `[ ${String(index + 1).padStart(2, '0')} ] Systems Snapshot`;
    return `[ ${String(index + 1).padStart(2, '0')} ] Mission Brief`;
}

export default function ModulePage() {
    const { courseId, slug } = useParams();
    const { user } = useAuth();
    const { stats, progress, dispatch, refetch } = useGame();

    const [phase, setPhase] = useState<Phase>('reading');
    const [activeQuestion, setActiveQuestion] = useState(0);
    const [quizResults, setQuizResults] = useState<{ questionId: string; correct: boolean }[]>([]);
    const [showLoginModal, setShowLoginModal] = useState(false);
    const [loginContext, setLoginContext] = useState<'quiz' | 'locked' | null>(null);

    useEffect(() => {
        if (user && showLoginModal) {
            setShowLoginModal(false);
            if (loginContext === 'quiz') {
                setPhase('quiz');
            }
            setLoginContext(null);
        }
    }, [user, showLoginModal, loginContext]);

    const course = (COURSES as Record<string, any>)[courseId || 'git'];
    const module = course?.modules.find((item: any) => item.slug === slug);
    const courseModules: Module[] = course?.modules ?? [];
    const currentModuleIndex = courseModules.findIndex((item: any) => item.slug === slug);
    const nextModule: Module | null = courseModules[currentModuleIndex + 1] ?? null;

    const questText =
        module?.content?.quest ??
        (module as any)?.quest ??
        'Complete this quest to level up your real-world dev workflow.';
    const contentSections = Array.isArray(module?.content?.sections) ? module.content.sections : [];

    const isLocked = module?.unlock_condition && progress[module.unlock_condition] !== 'completed';
    const currentQuestion: Question | null = module?.questions?.[activeQuestion] ?? null;
    const answeredPct = module?.questions?.length
        ? (quizResults.length / module.questions.length) * 100
        : 0;
    const correctCount = quizResults.filter((result) => result.correct).length;
    const regenMs = msUntilNextLife(
        stats?.lives ?? 0,
        stats?.max_lives ?? 3,
        stats?.last_life_regen ?? new Date().toISOString(),
    );
    const noLives = (stats?.lives ?? 3) <= 0;

    const missionMeta = useMemo(() => {
        const readingMinutes = Math.max(3, Math.ceil(contentSections.length * 1.6));
        return [
            { label: 'Level Gate', value: `LVL ${String((currentModuleIndex >= 0 ? currentModuleIndex : 0) + 1).padStart(2, '0')}` },
            { label: 'Est. Read Time', value: `${readingMinutes} MIN` },
            { label: 'Reward', value: `+${module?.xp_reward ?? 0} XP` },
        ];
    }, [contentSections.length, currentModuleIndex, module?.xp_reward]);

    const missionSidebar = useMemo(() => {
        return [
            { label: 'Pass Threshold', value: `${Math.round((module?.pass_threshold ?? 0) * 100)}%` },
            { label: 'Arena Nodes', value: `${module?.questions?.length ?? 0}` },
            { label: 'Badge Sync', value: module?.badge_id ? module.badge_id.replace(/-/g, ' ').toUpperCase() : 'PENDING' },
            { label: 'Prereq', value: module?.unlock_condition ? module.unlock_condition.replace(/-/g, ' ').toUpperCase() : 'OPEN ACCESS' },
        ];
    }, [module]);

    if (!module) {
        return (
            <div className="pb-32 pt-24">
                <SEO
                    title="Spawn Point Not Found — startzone.dev"
                    description="The quest route you entered does not map to a valid module."
                />
                <div className="mx-auto min-h-[70vh] max-w-6xl px-6">
                    <div className="flex min-h-[70vh] items-center justify-center">
                        <div className="w-full max-w-4xl border border-[#2d3554] bg-[#111111] px-8 py-12 text-center shadow-[0_0_0_1px_rgba(140,172,255,0.08)] md:px-14">
                            <div className="mx-auto mb-6 flex h-16 w-16 items-center justify-center border border-[#8cacff]/40 bg-[#8cacff]/10 font-['IBM_Plex_Mono'] text-2xl text-[#8cacff]">
                                404
                            </div>
                            <p className="mb-4 font-['IBM_Plex_Mono'] text-xs uppercase tracking-[0.38em] text-[#8cacff]">
                                INVALID QUEST ROUTE
                            </p>
                            <h1 className="mb-5 font-['Space_Grotesk'] text-4xl font-black uppercase italic text-white md:text-6xl">
                                Spawn point not found
                            </h1>
                            <p className="mx-auto mb-8 max-w-2xl text-lg leading-relaxed text-[#acabaa]">
                                <span className="font-['IBM_Plex_Mono'] text-[#dedede]">/{courseId}/{slug}</span> does not resolve to an active mission feed.
                                Pull a valid route from the quest map and re-enter the grid.
                            </p>
                            <div className="flex flex-col justify-center gap-4 sm:flex-row">
                                <Link
                                    to={course ? `/courses/${courseId}` : '/courses'}
                                    className="inline-flex min-w-[220px] items-center justify-center border border-[#8cacff] bg-[#171d2e] px-8 py-4 font-['Space_Grotesk'] text-sm font-black uppercase tracking-[0.2em] text-white transition-colors hover:bg-[#202845]"
                                >
                                    {course ? 'Return To Quest Map' : 'Browse Courses'}
                                </Link>
                                <Link
                                    to="/courses"
                                    className="inline-flex min-w-[220px] items-center justify-center border border-[#2a2a2a] bg-[#161616] px-8 py-4 font-['Space_Grotesk'] text-sm font-black uppercase tracking-[0.2em] text-[#dedede] transition-colors hover:bg-[#1c1c1c]"
                                >
                                    All Courses
                                </Link>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    if (isLocked) {
        const unlockedTitle =
            courseModules.find((item: any) => item.slug === module.unlock_condition)?.title ??
            module.unlock_condition!.replace(/-/g, ' ');

        if (!user) {
            return (
                <div className="pb-32 pt-24">
                    <SEO title={`${module.title} — startzone.dev`} description={questText} />
                    <div className="mx-auto max-w-6xl px-6">
                        <div className="border border-[#2a2a2a] bg-[#111111] px-8 py-12 md:px-12">
                            <div className="mb-8 flex flex-wrap items-center justify-between gap-4">
                                <div>
                                    <p className="font-['IBM_Plex_Mono'] text-xs uppercase tracking-[0.34em] text-[#8cacff]">
                                        IDENTITY REQUIRED
                                    </p>
                                    <h1 className="mt-3 font-['Space_Grotesk'] text-4xl font-black uppercase italic text-white md:text-6xl">
                                        Access Pending
                                    </h1>
                                </div>
                                <div className="border border-[#8cacff]/40 bg-[#8cacff]/8 px-4 py-2 font-['IBM_Plex_Mono'] text-xs uppercase tracking-[0.28em] text-[#8cacff]">
                                    PROFILE SYNC
                                </div>
                            </div>
                            <div className="grid gap-6 md:grid-cols-[1.6fr_0.9fr]">
                                <div className="border border-[#242424] bg-[#141414] p-7">
                                    <p className="font-['IBM_Plex_Mono'] text-xs uppercase tracking-[0.32em] text-[#00cffc]">
                                        ACCESS GATE
                                    </p>
                                    <p className="mt-5 text-lg leading-relaxed text-[#dedede]">
                                        This sector path is tied to player progression. Sign in to initialize your profile,
                                        sync unlock state, and verify whether{' '}
                                        <span className="font-['Space_Grotesk'] font-bold uppercase text-[#00cffc]">
                                            {unlockedTitle}
                                        </span>{' '}
                                        has been cleared on your route.
                                    </p>
                                </div>
                                <div className="border border-[#242424] bg-[#141414] p-7">
                                    <p className="font-['IBM_Plex_Mono'] text-xs uppercase tracking-[0.32em] text-[#d674ff]">
                                        ROUTE OPTIONS
                                    </p>
                                    <div className="mt-5 space-y-4">
                                        <button
                                            type="button"
                                            onClick={() => {
                                                setLoginContext('locked');
                                                setShowLoginModal(true);
                                            }}
                                            className="flex w-full items-center justify-between border border-[#8cacff] bg-[#1a2236] px-5 py-4 font-['Space_Grotesk'] text-sm font-black uppercase tracking-[0.18em] text-white transition-colors hover:bg-[#212b46]"
                                        >
                                            Initialize Identity
                                            <span className="font-['IBM_Plex_Mono'] text-[#8cacff]">01</span>
                                        </button>
                                        <Link
                                            to={`/courses/${courseId}`}
                                            className="flex items-center justify-between border border-[#2a2a2a] bg-[#161616] px-5 py-4 font-['Space_Grotesk'] text-sm font-black uppercase tracking-[0.18em] text-[#dedede] transition-colors hover:bg-[#1b1b1b]"
                                        >
                                            Return To Quest Map
                                            <span className="font-['IBM_Plex_Mono'] text-[#767575]">02</span>
                                        </Link>
                                        <Link
                                            to="/courses"
                                            className="flex items-center justify-between border border-[#2a2a2a] bg-[#161616] px-5 py-4 font-['Space_Grotesk'] text-sm font-black uppercase tracking-[0.18em] text-[#dedede] transition-colors hover:bg-[#1b1b1b]"
                                        >
                                            Browse Other Sectors
                                            <span className="font-['IBM_Plex_Mono'] text-[#767575]">03</span>
                                        </Link>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <LoginModal
                        isOpen={showLoginModal}
                        onClose={() => {
                            setShowLoginModal(false);
                            setLoginContext(null);
                        }}
                    />
                </div>
            );
        }

        return (
            <div className="pb-32 pt-24">
                <SEO title={`${module.title} — startzone.dev`} description={questText} />
                <div className="mx-auto max-w-6xl px-6">
                    <div className="border border-[#2a2a2a] bg-[#111111] px-8 py-12 md:px-12">
                        <div className="mb-8 flex flex-wrap items-center justify-between gap-4">
                            <div>
                                <p className="font-['IBM_Plex_Mono'] text-xs uppercase tracking-[0.34em] text-[#ff716c]">
                                    MISSION LOCKED
                                </p>
                                <h1 className="mt-3 font-['Space_Grotesk'] text-4xl font-black uppercase italic text-white md:text-6xl">
                                    Access Denied
                                </h1>
                            </div>
                            <div className="border border-[#ff716c]/40 bg-[#ff716c]/8 px-4 py-2 font-['IBM_Plex_Mono'] text-xs uppercase tracking-[0.28em] text-[#ff716c]">
                                PREREQ REQUIRED
                            </div>
                        </div>
                        <div className="grid gap-6 md:grid-cols-[1.6fr_0.9fr]">
                            <div className="border border-[#242424] bg-[#141414] p-7">
                                <p className="font-['IBM_Plex_Mono'] text-xs uppercase tracking-[0.32em] text-[#8cacff]">
                                    UNLOCK CONDITION
                                </p>
                                <p className="mt-5 text-lg leading-relaxed text-[#dedede]">
                                    You have not cleared the prerequisite chain for this sector. Complete{' '}
                                    <span className="font-['Space_Grotesk'] font-bold uppercase text-[#00cffc]">
                                        {unlockedTitle}
                                    </span>{' '}
                                    to restore access.
                                </p>
                            </div>
                            <div className="border border-[#242424] bg-[#141414] p-7">
                                <p className="font-['IBM_Plex_Mono'] text-xs uppercase tracking-[0.32em] text-[#d674ff]">
                                    ROUTE OPTIONS
                                </p>
                                <div className="mt-5 space-y-4">
                                    <Link
                                        to={`/courses/${courseId}`}
                                        className="flex items-center justify-between border border-[#8cacff] bg-[#1a2236] px-5 py-4 font-['Space_Grotesk'] text-sm font-black uppercase tracking-[0.18em] text-white transition-colors hover:bg-[#212b46]"
                                    >
                                        Return To Quest Map
                                        <span className="font-['IBM_Plex_Mono'] text-[#8cacff]">01</span>
                                    </Link>
                                    <Link
                                        to="/courses"
                                        className="flex items-center justify-between border border-[#2a2a2a] bg-[#161616] px-5 py-4 font-['Space_Grotesk'] text-sm font-black uppercase tracking-[0.18em] text-[#dedede] transition-colors hover:bg-[#1b1b1b]"
                                    >
                                        Browse Other Sectors
                                        <span className="font-['IBM_Plex_Mono'] text-[#767575]">02</span>
                                    </Link>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    const handleStartQuiz = () => {
        if (!user) {
            setLoginContext('quiz');
            setShowLoginModal(true);
            return;
        }
        if (noLives) return;
        setPhase('quiz');
    };

    const handleGuestProceed = () => {
        setShowLoginModal(false);
        if (loginContext === 'quiz') {
            setPhase('quiz');
        }
        setLoginContext(null);
    };

    const handleQuestionAnswered = (res: any) => {
        if (!currentQuestion) return;

        const newResults = [...quizResults, { questionId: currentQuestion.id, correct: res.correct }];
        setQuizResults(newResults);

        if (activeQuestion < module.questions.length - 1) {
            setActiveQuestion((prev) => prev + 1);
            return;
        }

        const totalCorrect = newResults.filter((entry) => entry.correct).length;
        const pass = totalCorrect >= Math.ceil(module.questions.length * module.pass_threshold);

        if (pass) {
            setPhase('complete');
            if (user) {
                void refetch().catch(() => {});
            } else {
                dispatch({ type: 'UPDATE_PROGRESS', payload: { [module.slug]: 'completed' } });
            }
            return;
        }

        setPhase('fail');
    };

    const moduleSchema = JSON.stringify({
        '@context': 'https://schema.org',
        '@type': 'Course',
        name: module.title,
        description: questText,
        provider: {
            '@type': 'Organization',
            name: 'startzone.dev',
            sameAs: 'https://startzone.dev',
        },
    });

    return (
        <div className="pb-32 pt-16">
            <SEO
                title={`${module.title} — startzone.dev`}
                description={questText}
                schema={moduleSchema}
                canonicalUrl={`https://startzone.dev/courses/${courseId}/${slug}`}
            />

            <div className="sticky top-16 z-40 border-b border-[#202020] bg-[#101010]/92 backdrop-blur-md">
                <div className="mx-auto flex max-w-7xl items-center justify-between gap-6 px-6 py-4">
                    <div className="flex min-w-0 items-center gap-4">
                        <Link
                            to={`/courses/${courseId}`}
                            className="flex h-11 w-11 items-center justify-center border border-[#2f3859] bg-[#131929] font-['IBM_Plex_Mono'] text-sm text-[#8cacff] transition-colors hover:bg-[#18203a]"
                            aria-label="Back to quest map"
                        >
                            &lt;
                        </Link>
                        <div className="min-w-0">
                            <p className="truncate font-['IBM_Plex_Mono'] text-[11px] uppercase tracking-[0.34em] text-[#8cacff]">
                                Mission Feed // {course?.title ?? 'Quest Map'}
                            </p>
                            <div className="mt-2 flex flex-wrap items-center gap-3">
                                <h4 className="truncate font-['Space_Grotesk'] text-xl font-black uppercase italic text-white">
                                    {module.title}
                                </h4>
                                <span className="font-['IBM_Plex_Mono'] text-[11px] uppercase tracking-[0.3em] text-[#767575]">
                                    {phase === 'reading' ? 'Briefing Active' : `Node ${activeQuestion + 1}/${module.questions.length}`}
                                </span>
                            </div>
                        </div>
                    </div>
                    <div className="hidden min-w-[280px] items-center gap-4 md:flex">
                        <div className="h-[3px] flex-1 overflow-hidden bg-[#232323]">
                            <motion.div
                                animate={{ width: phase === 'reading' ? '0%' : `${answeredPct}%` }}
                                transition={{ type: 'spring', stiffness: 120, damping: 20 }}
                                className="h-full bg-[#00cffc]"
                            />
                        </div>
                        <div className="grid grid-cols-2 gap-x-5 gap-y-1 font-['IBM_Plex_Mono'] text-[11px] uppercase tracking-[0.24em] text-[#767575]">
                            <span className="text-[#8cacff]">Lives {String(stats?.lives ?? 0).padStart(2, '0')}</span>
                            <span>Pass {Math.round(module.pass_threshold * 100)}%</span>
                            <span>XP +{module.xp_reward}</span>
                            <span>{quizResults.length}/{module.questions.length} cleared</span>
                        </div>
                    </div>
                </div>
            </div>

            <div className="mx-auto mt-12 max-w-7xl px-6">
                <AnimatePresence mode="wait">
                    {phase === 'reading' && (
                        <motion.div
                            key={noLives ? 'reading-no-lives' : 'reading'}
                            initial={{ opacity: 0, y: 18 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -18 }}
                            className="space-y-10"
                        >
                            {noLives ? (
                                <div className="space-y-8">
                                    <section className="relative overflow-hidden border border-[#2a2a2a] bg-[#111111] px-8 py-10 md:px-12 md:py-12">
                                        <div className="absolute inset-y-0 left-[9%] w-px bg-[#8cacff]/7" />
                                        <div className="absolute inset-y-0 right-[11%] w-px bg-[#ff716c]/7" />
                                        <div className="max-w-4xl">
                                            <div className="inline-flex items-center gap-3 border border-[#ff716c]/30 bg-[#ff716c]/10 px-4 py-2 font-['IBM_Plex_Mono'] text-xs uppercase tracking-[0.34em] text-[#ff716c]">
                                                <span className="h-2.5 w-2.5 rounded-full bg-[#ff716c]" />
                                                System Critical
                                            </div>
                                            <p className="mt-6 font-['IBM_Plex_Mono'] text-xs uppercase tracking-[0.32em] text-[#8cacff]">
                                                LOG_{module.slug.toUpperCase()} // LIFE_POOL_DEPLETED
                                            </p>
                                            <h1 className="mt-5 font-['Space_Grotesk'] text-[3.25rem] font-black uppercase italic leading-[0.86] text-white md:text-[6.4rem]">
                                                No Lives
                                                <br />
                                                <span className="text-[#ff716c]">Remaining</span>
                                            </h1>
                                            <p className="mt-7 max-w-3xl text-lg leading-relaxed text-[#acabaa]">
                                                Your last arena run consumed the remaining life pool. Restore vitals before
                                                re-entering this mission node.
                                            </p>
                                        </div>
                                    </section>

                                    <div className="grid gap-8 xl:grid-cols-[1.1fr_0.9fr]">
                                        <section className="border border-[#2a2a2a] bg-[#121212] p-8">
                                            <div className="mb-8 flex items-start justify-between gap-4">
                                                <div>
                                                    <p className="font-['IBM_Plex_Mono'] text-xs uppercase tracking-[0.34em] text-[#8cacff]">
                                                        Respawn Countdown
                                                    </p>
                                                    <h2 className="mt-3 font-['Space_Grotesk'] text-3xl font-black uppercase italic text-white">
                                                        Regen Window
                                                    </h2>
                                                </div>
                                                <div className="border border-[#2f3859] bg-[#171d2e] px-4 py-3 font-['IBM_Plex_Mono'] text-sm uppercase tracking-[0.3em] text-[#8cacff]">
                                                    {formatMs(regenMs)}
                                                </div>
                                            </div>
                                            <div className="h-4 overflow-hidden border border-[#2c3555] bg-[#0f1522]">
                                                <div
                                                    className="h-full bg-gradient-to-r from-[#8cacff] via-[#00cffc] to-[#d674ff]"
                                                    style={{
                                                        width: `${Math.max(
                                                            14,
                                                            Math.round(((stats?.max_lives ?? 3) - (stats?.lives ?? 0)) / Math.max(1, stats?.max_lives ?? 3) * 100),
                                                        )}%`,
                                                    }}
                                                />
                                            </div>
                                            <div className="mt-4 flex flex-wrap items-center justify-between gap-3 font-['IBM_Plex_Mono'] text-xs uppercase tracking-[0.28em] text-[#767575]">
                                                <span>Life Pool {stats?.lives ?? 0}/{stats?.max_lives ?? 3}</span>
                                                <span>{regenMs === null ? 'Vitals Full Soon' : 'Auto-regen +1 every 4h'}</span>
                                            </div>

                                            <div className="mt-10 grid gap-4 sm:grid-cols-3">
                                                <button
                                                    type="button"
                                                    className="flex min-h-[96px] flex-col justify-between border border-[#8cacff] bg-[#1a2236] p-5 text-left transition-colors hover:bg-[#202948]"
                                                >
                                                    <span className="font-['IBM_Plex_Mono'] text-[11px] uppercase tracking-[0.28em] text-[#8cacff]">
                                                        Restore Path 01
                                                    </span>
                                                    <span className="font-['Space_Grotesk'] text-lg font-black uppercase text-white">
                                                        Spend XP
                                                    </span>
                                                </button>
                                                <button
                                                    type="button"
                                                    className="flex min-h-[96px] flex-col justify-between border border-[#2a2a2a] bg-[#161616] p-5 text-left transition-colors hover:bg-[#1d1d1d]"
                                                >
                                                    <span className="font-['IBM_Plex_Mono'] text-[11px] uppercase tracking-[0.28em] text-[#00cffc]">
                                                        Restore Path 02
                                                    </span>
                                                    <span className="font-['Space_Grotesk'] text-lg font-black uppercase text-white">
                                                        Share Status
                                                    </span>
                                                </button>
                                                <button
                                                    type="button"
                                                    className="flex min-h-[96px] flex-col justify-between border border-[#2a2a2a] bg-[#161616] p-5 text-left transition-colors hover:bg-[#1d1d1d]"
                                                >
                                                    <span className="font-['IBM_Plex_Mono'] text-[11px] uppercase tracking-[0.28em] text-[#d674ff]">
                                                        Restore Path 03
                                                    </span>
                                                    <span className="font-['Space_Grotesk'] text-lg font-black uppercase text-white">
                                                        Watch Recap
                                                    </span>
                                                </button>
                                            </div>
                                        </section>

                                        <section className="space-y-6">
                                            <div className="border border-[#2a2a2a] bg-[#121212] p-7">
                                                <p className="font-['IBM_Plex_Mono'] text-xs uppercase tracking-[0.34em] text-[#8cacff]">
                                                    Last Session Analysis
                                                </p>
                                                <div className="mt-6 grid gap-4 sm:grid-cols-3 xl:grid-cols-1">
                                                    <div className="border border-[#242424] bg-[#151515] p-5">
                                                        <p className="font-['IBM_Plex_Mono'] text-[11px] uppercase tracking-[0.28em] text-[#767575]">
                                                            Mission Node
                                                        </p>
                                                        <p className="mt-3 font-['Space_Grotesk'] text-2xl font-black uppercase italic text-white">
                                                            {module.title}
                                                        </p>
                                                    </div>
                                                    <div className="border border-[#242424] bg-[#151515] p-5">
                                                        <p className="font-['IBM_Plex_Mono'] text-[11px] uppercase tracking-[0.28em] text-[#767575]">
                                                            Arena Count
                                                        </p>
                                                        <p className="mt-3 font-['Space_Grotesk'] text-2xl font-black uppercase text-[#00cffc]">
                                                            {module.questions.length}
                                                        </p>
                                                    </div>
                                                    <div className="border border-[#242424] bg-[#151515] p-5">
                                                        <p className="font-['IBM_Plex_Mono'] text-[11px] uppercase tracking-[0.28em] text-[#767575]">
                                                            Streak Sync
                                                        </p>
                                                        <p className="mt-3 font-['Space_Grotesk'] text-2xl font-black uppercase text-[#d674ff]">
                                                            {stats?.streak_days ?? 0} Days
                                                        </p>
                                                    </div>
                                                </div>
                                            </div>

                                            <div className="border border-[#2a2a2a] bg-[#121212] p-7">
                                                <p className="font-['IBM_Plex_Mono'] text-xs uppercase tracking-[0.34em] text-[#ff716c]">
                                                    Module Lock
                                                </p>
                                                <div className="mt-5 space-y-4">
                                                    <div className="border border-[#242424] bg-[#151515] p-5">
                                                        <p className="font-['IBM_Plex_Mono'] text-[11px] uppercase tracking-[0.28em] text-[#767575]">
                                                            Awaiting Re-entry
                                                        </p>
                                                        <p className="mt-3 text-sm leading-relaxed text-[#acabaa]">
                                                            This mission stays locked until at least one life is restored to your pool.
                                                        </p>
                                                    </div>
                                                    <div className="border border-[#242424] bg-[#151515] p-5">
                                                        <p className="font-['IBM_Plex_Mono'] text-[11px] uppercase tracking-[0.28em] text-[#767575]">
                                                            Mission Debrief
                                                        </p>
                                                        <p className="mt-3 text-sm leading-relaxed text-[#acabaa]">
                                                            Review the briefing again, then re-enter the arena when vitals sync.
                                                        </p>
                                                    </div>
                                                </div>
                                            </div>
                                        </section>
                                    </div>

                                    <div className="flex flex-wrap gap-4">
                                        <Link
                                            to={`/courses/${courseId}`}
                                            className="inline-flex min-w-[230px] items-center justify-center border border-[#8cacff] bg-[#1a2236] px-6 py-4 font-['Space_Grotesk'] text-sm font-black uppercase tracking-[0.2em] text-white transition-colors hover:bg-[#222c49]"
                                        >
                                            Return To Quest Map
                                        </Link>
                                        <Link
                                            to="/dashboard"
                                            className="inline-flex min-w-[230px] items-center justify-center border border-[#2a2a2a] bg-[#161616] px-6 py-4 font-['Space_Grotesk'] text-sm font-black uppercase tracking-[0.2em] text-[#dedede] transition-colors hover:bg-[#1d1d1d]"
                                        >
                                            Return To Base Camp
                                        </Link>
                                    </div>
                                </div>
                            ) : (
                                <>
                                    <section className="relative overflow-hidden border border-[#232323] bg-[#101010] px-8 py-10 md:px-12 md:py-14">
                                        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top,rgba(140,172,255,0.08),transparent_55%)]" />
                                        <div className="absolute inset-y-0 left-[12%] w-px bg-[#8cacff]/8" />
                                        <div className="absolute inset-y-0 right-[13%] w-px bg-[#00cffc]/8" />
                                        <div className="relative">
                                            <div className="mb-8 flex flex-wrap items-center justify-between gap-4">
                                                <div className="flex items-center gap-3">
                                                    <span className="h-3 w-3 rounded-full bg-[#00cffc]" />
                                                    <span className="font-['IBM_Plex_Mono'] text-xs uppercase tracking-[0.34em] text-[#8cacff]">
                                                        MISSION ONLINE
                                                    </span>
                                                </div>
                                                <div className="font-['IBM_Plex_Mono'] text-xs uppercase tracking-[0.32em] text-[#767575]">
                                                    CID // {module.slug.toUpperCase()}
                                                </div>
                                            </div>
                                            <div className="grid gap-8 xl:grid-cols-[1.15fr_0.85fr]">
                                                <div>
                                                    <h1 className="font-['Space_Grotesk'] text-[2.8rem] font-black uppercase italic leading-[0.9] text-white md:text-[5.4rem]">
                                                        {module.title.split(' ').slice(0, 2).join(' ')}
                                                        <br />
                                                        <span className="text-[#8cacff]">
                                                            {module.title.split(' ').slice(2).join(' ') || 'Mission'}
                                                        </span>
                                                    </h1>
                                                    <p className="mt-8 max-w-3xl text-lg leading-relaxed text-[#acabaa]">
                                                        {questText}
                                                    </p>
                                                    <div className="mt-8 grid gap-4 sm:grid-cols-3">
                                                        {missionMeta.map((item) => (
                                                            <div key={item.label} className="border border-[#242424] bg-[#151515] px-5 py-4">
                                                                <p className="font-['IBM_Plex_Mono'] text-[11px] uppercase tracking-[0.28em] text-[#767575]">
                                                                    {item.label}
                                                                </p>
                                                                <p className="mt-3 font-['Space_Grotesk'] text-2xl font-black uppercase text-white">
                                                                    {item.value}
                                                                </p>
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>

                                                <aside className="space-y-5 border border-[#242424] bg-[#131313] p-6 md:p-7">
                                                    <div>
                                                        <p className="font-['IBM_Plex_Mono'] text-xs uppercase tracking-[0.34em] text-[#00cffc]">
                                                            Critical Intel
                                                        </p>
                                                        <p className="mt-4 text-sm leading-relaxed text-[#acabaa]">
                                                            Use the briefing feed to lock the concept chain before you enter the arena.
                                                            Quiz validation begins immediately after the first answer.
                                                        </p>
                                                    </div>
                                                    <div className="space-y-3">
                                                        {missionSidebar.map((item) => (
                                                            <div key={item.label} className="flex items-center justify-between gap-4 border border-[#222] bg-[#151515] px-4 py-3">
                                                                <span className="font-['IBM_Plex_Mono'] text-[10px] uppercase tracking-[0.24em] text-[#767575]">
                                                                    {item.label}
                                                                </span>
                                                                <span className="font-['Space_Grotesk'] text-sm font-black uppercase text-white">
                                                                    {item.value}
                                                                </span>
                                                            </div>
                                                        ))}
                                                    </div>
                                                    <div className="border border-[#2f3859] bg-[#171d2e] p-4">
                                                        <p className="font-['IBM_Plex_Mono'] text-[11px] uppercase tracking-[0.24em] text-[#8cacff]">
                                                            Achievement Sync
                                                        </p>
                                                        <p className="mt-3 font-['Space_Grotesk'] text-base font-black uppercase text-white">
                                                            {module.badge_id ? `Unlock ${module.badge_id.replace(/-/g, ' ')}` : 'Mission Badge Pending'}
                                                        </p>
                                                    </div>
                                                    {module.concept_refs?.length ? (
                                                        <div>
                                                            <p className="font-['IBM_Plex_Mono'] text-[11px] uppercase tracking-[0.24em] text-[#d674ff]">
                                                                Concept Links
                                                            </p>
                                                            <div className="mt-4 flex flex-wrap gap-2">
                                                                {module.concept_refs.map((id: string) => (
                                                                    <ConceptTag key={id} conceptId={id} label={id.replace(/-/g, ' ')} />
                                                                ))}
                                                            </div>
                                                        </div>
                                                    ) : null}
                                                </aside>
                                            </div>
                                        </div>
                                    </section>

                                    <div className="grid gap-8 xl:grid-cols-[1.1fr_0.9fr]">
                                        <section className="space-y-8">
                                            {contentSections.length ? (
                                                contentSections.map((section: any, idx: number) => {
                                                    const callout = section.type === 'explanation' ? getSectionCallout(section.body) : null;

                                                    return (
                                                        <article key={`${section.type}-${idx}`} className="border border-[#242424] bg-[#121212] p-7 md:p-8">
                                                            <p className="font-['IBM_Plex_Mono'] text-xs uppercase tracking-[0.32em] text-[#8cacff]">
                                                                {getSectionHeading(section.type, idx)}
                                                            </p>

                                                            {section.type === 'analogy' && (
                                                                <div className="mt-6 border-l-2 border-[#00cffc] bg-[#131a1d] px-5 py-6">
                                                                    <p className="font-['Space_Grotesk'] text-2xl font-bold italic leading-relaxed text-white">
                                                                        {section.body}
                                                                    </p>
                                                                </div>
                                                            )}

                                                            {section.type === 'explanation' && callout && (
                                                                <div className={cn('mt-6 border p-5 md:p-6', callout.accentClass)}>
                                                                    <div className="mb-4 inline-flex items-center gap-3">
                                                                        <span className={cn("border px-3 py-1 font-['IBM_Plex_Mono'] text-[11px] uppercase tracking-[0.24em]", callout.badgeClass)}>
                                                                            {callout.label}
                                                                        </span>
                                                                    </div>
                                                                    <p className={cn('text-base leading-relaxed md:text-lg', callout.bodyClass, callout.tone === 'tip' ? 'italic' : '')}>
                                                                        {callout.content}
                                                                    </p>
                                                                </div>
                                                            )}

                                                            {section.type === 'explanation' && !callout && (
                                                                <div className="mt-6 space-y-4 text-base leading-relaxed text-[#acabaa] md:text-lg">
                                                                    {section.body ? (
                                                                        <p>{section.body}</p>
                                                                    ) : (
                                                                        <p className="font-['IBM_Plex_Mono'] text-sm uppercase tracking-[0.22em] text-[#767575]">
                                                                            Referencing concept file: {section.concept_id}
                                                                        </p>
                                                                    )}
                                                                </div>
                                                            )}

                                                            {section.type === 'code' && (
                                                                <div className="mt-6 overflow-hidden border border-[#2d3554] bg-[#0d121d]">
                                                                    <div className="flex items-center justify-between border-b border-[#222b44] bg-[#151d2f] px-4 py-3">
                                                                        <span className="font-['IBM_Plex_Mono'] text-[11px] uppercase tracking-[0.3em] text-[#8cacff]">
                                                                            {section.lang || 'shell'}
                                                                        </span>
                                                                        <div className="flex gap-2">
                                                                            <span className="h-2.5 w-2.5 rounded-full bg-[#ff716c]" />
                                                                            <span className="h-2.5 w-2.5 rounded-full bg-[#00cffc]" />
                                                                        </div>
                                                                    </div>
                                                                    <pre className="overflow-x-auto px-5 py-5 font-['IBM_Plex_Mono'] text-sm leading-7 text-[#dedede]">
                                                                        <code>{section.body}</code>
                                                                    </pre>
                                                                </div>
                                                            )}
                                                        </article>
                                                    );
                                                })
                                            ) : (
                                                <article className="border border-[#242424] bg-[#121212] p-7 md:p-8">
                                                    <p className="font-['IBM_Plex_Mono'] text-xs uppercase tracking-[0.32em] text-[#8cacff]">
                                                        [ 01 ] Mission Brief
                                                    </p>
                                                    <p className="mt-6 text-lg leading-relaxed text-[#acabaa]">
                                                        Briefing feed is clear. Enter the arena to validate your sync against this mission.
                                                    </p>
                                                </article>
                                            )}
                                        </section>

                                        <aside className="space-y-8">
                                            <section className="border border-[#242424] bg-[#121212] p-7">
                                                <p className="font-['IBM_Plex_Mono'] text-xs uppercase tracking-[0.34em] text-[#d674ff]">
                                                    Mission Parameters
                                                </p>
                                                <div className="mt-6 space-y-4">
                                                    {missionSidebar.map((item) => (
                                                        <div key={item.label} className="flex items-center justify-between gap-3 border-b border-[#202020] pb-4">
                                                            <span className="font-['IBM_Plex_Mono'] text-[11px] uppercase tracking-[0.24em] text-[#767575]">
                                                                {item.label}
                                                            </span>
                                                            <span className="font-['Space_Grotesk'] text-sm font-black uppercase text-white">
                                                                {item.value}
                                                            </span>
                                                        </div>
                                                    ))}
                                                </div>
                                            </section>

                                            <section className="border border-[#242424] bg-[#121212] p-7">
                                                <p className="font-['IBM_Plex_Mono'] text-xs uppercase tracking-[0.34em] text-[#00cffc]">
                                                    Live Status
                                                </p>
                                                <div className="mt-6 grid gap-4 sm:grid-cols-2 xl:grid-cols-1">
                                                    <div className="border border-[#242424] bg-[#151515] p-5">
                                                        <p className="font-['IBM_Plex_Mono'] text-[11px] uppercase tracking-[0.24em] text-[#767575]">
                                                            Life Pool
                                                        </p>
                                                        <p className="mt-3 font-['Space_Grotesk'] text-3xl font-black uppercase text-[#ff716c]">
                                                            {stats?.lives ?? 0}/{stats?.max_lives ?? 3}
                                                        </p>
                                                    </div>
                                                    <div className="border border-[#242424] bg-[#151515] p-5">
                                                        <p className="font-['IBM_Plex_Mono'] text-[11px] uppercase tracking-[0.24em] text-[#767575]">
                                                            Streak Sync
                                                        </p>
                                                        <p className="mt-3 font-['Space_Grotesk'] text-3xl font-black uppercase text-[#d674ff]">
                                                            {stats?.streak_days ?? 0}D
                                                        </p>
                                                    </div>
                                                    <div className="border border-[#242424] bg-[#151515] p-5">
                                                        <p className="font-['IBM_Plex_Mono'] text-[11px] uppercase tracking-[0.24em] text-[#767575]">
                                                            Regen ETA
                                                        </p>
                                                        <p className="mt-3 font-['Space_Grotesk'] text-2xl font-black uppercase text-[#8cacff]">
                                                            {formatMs(regenMs)}
                                                        </p>
                                                    </div>
                                                </div>
                                            </section>
                                        </aside>
                                    </div>

                                    <section className="border border-[#2a2a2a] bg-[#111111] px-8 py-10 text-center md:px-12">
                                        <div className="mx-auto max-w-3xl">
                                            <p className="font-['IBM_Plex_Mono'] text-xs uppercase tracking-[0.34em] text-[#8cacff]">
                                                End Of Briefing Feed
                                            </p>
                                            <h2 className="mt-5 font-['Space_Grotesk'] text-4xl font-black uppercase italic text-white md:text-5xl">
                                                Initiate Arena Task
                                            </h2>
                                            <p className="mx-auto mt-5 max-w-2xl text-lg leading-relaxed text-[#acabaa]">
                                                Clear {Math.round(module.pass_threshold * 100)}% of the arena nodes to bank{' '}
                                                <span className="font-black text-[#00cffc]">+{module.xp_reward} XP</span> and unlock the next route.
                                            </p>
                                            <div className="mt-8 flex flex-col justify-center gap-4 sm:flex-row">
                                                <button
                                                    onClick={handleStartQuiz}
                                                    aria-label="Start Quiz"
                                                    className="inline-flex min-w-[250px] items-center justify-center border border-[#8cacff] bg-[#1a2236] px-8 py-5 font-['Space_Grotesk'] text-sm font-black uppercase tracking-[0.22em] text-white transition-colors hover:bg-[#222c49]"
                                                >
                                                    Start Quiz // Initiate Arena Task
                                                </button>
                                                <Link
                                                    to={`/courses/${courseId}`}
                                                    className="inline-flex min-w-[250px] items-center justify-center border border-[#2a2a2a] bg-[#161616] px-8 py-5 font-['Space_Grotesk'] text-sm font-black uppercase tracking-[0.22em] text-[#dedede] transition-colors hover:bg-[#1d1d1d]"
                                                >
                                                    Back To Quest Map
                                                </Link>
                                            </div>
                                        </div>
                                    </section>
                                </>
                            )}
                        </motion.div>
                    )}

                    {phase === 'quiz' && currentQuestion && (
                        <motion.div
                            key="quiz"
                            initial={{ opacity: 0, scale: 0.96 }}
                            animate={{ opacity: 1, scale: 1 }}
                            exit={{ opacity: 0, scale: 1.03 }}
                            className="mx-auto max-w-5xl"
                        >
                            <QuestionBlock key={currentQuestion.id} question={currentQuestion} onAnswered={handleQuestionAnswered} />
                        </motion.div>
                    )}

                    {phase === 'complete' && (
                        <motion.div
                            key="complete"
                            initial={{ opacity: 0, y: 22 }}
                            animate={{ opacity: 1, y: 0 }}
                            className="mx-auto max-w-4xl space-y-8"
                        >
                            <section className="border border-[#2a2a2a] bg-[#111111] px-8 py-12 text-center md:px-14">
                                <p className="font-['IBM_Plex_Mono'] text-xs uppercase tracking-[0.34em] text-[#00cffc]">
                                    Arena Cleared
                                </p>
                                <h1 className="mt-5 font-['Space_Grotesk'] text-5xl font-black uppercase italic text-white md:text-7xl">
                                    Mission
                                    <span className="text-[#00cffc]"> Complete</span>
                                </h1>
                                <p className="mx-auto mt-6 max-w-2xl text-lg leading-relaxed text-[#acabaa]">
                                    You locked in {correctCount}/{module.questions.length} nodes and banked{' '}
                                    <span className="font-black text-[#00cffc]">+{module.xp_reward} XP</span>.
                                </p>
                            </section>

                            <section className="grid gap-5 md:grid-cols-2">
                                {nextModule ? (
                                    <Link
                                        to={`/courses/${courseId}/${nextModule.slug}`}
                                        className="flex min-h-[150px] flex-col justify-between border border-[#8cacff] bg-[#171d2e] px-7 py-6 transition-colors hover:bg-[#202845]"
                                    >
                                        <span className="font-['IBM_Plex_Mono'] text-xs uppercase tracking-[0.28em] text-[#8cacff]">
                                            Next Mission
                                        </span>
                                        <span className="font-['Space_Grotesk'] text-2xl font-black uppercase italic text-white">
                                            {nextModule.title}
                                        </span>
                                    </Link>
                                ) : (
                                    <div className="flex min-h-[150px] flex-col justify-between border border-[#00cffc]/30 bg-[#00cffc]/8 px-7 py-6">
                                        <span className="font-['IBM_Plex_Mono'] text-xs uppercase tracking-[0.28em] text-[#00cffc]">
                                            Course Status
                                        </span>
                                        <span className="font-['Space_Grotesk'] text-2xl font-black uppercase italic text-white">
                                            Entire Course Cleared
                                        </span>
                                    </div>
                                )}
                                <Link
                                    to={`/courses/${courseId}`}
                                    className="flex min-h-[150px] flex-col justify-between border border-[#2a2a2a] bg-[#161616] px-7 py-6 transition-colors hover:bg-[#1d1d1d]"
                                >
                                    <span className="font-['IBM_Plex_Mono'] text-xs uppercase tracking-[0.28em] text-[#767575]">
                                        Route Index
                                    </span>
                                    <span className="font-['Space_Grotesk'] text-2xl font-black uppercase italic text-white">
                                        Return To Quest Map
                                    </span>
                                </Link>
                            </section>
                        </motion.div>
                    )}

                    {phase === 'fail' && (
                        <motion.div
                            key="fail"
                            initial={{ opacity: 0, y: 22 }}
                            animate={{ opacity: 1, y: 0 }}
                            className="mx-auto max-w-4xl space-y-8"
                        >
                            <section className="border border-[#2a2a2a] bg-[#111111] px-8 py-12 text-center md:px-14">
                                <p className="font-['IBM_Plex_Mono'] text-xs uppercase tracking-[0.34em] text-[#ff716c]">
                                    Arena Crash Report
                                </p>
                                <h1 className="mt-5 font-['Space_Grotesk'] text-5xl font-black uppercase italic text-white md:text-7xl">
                                    Validation
                                    <span className="text-[#ff716c]"> Failed</span>
                                </h1>
                                <p className="mx-auto mt-6 max-w-2xl text-lg leading-relaxed text-[#acabaa]">
                                    You cleared {correctCount}/{module.questions.length} nodes. Re-run the briefing and target at least{' '}
                                    <span className="font-black text-[#8cacff]">{Math.round(module.pass_threshold * 100)}%</span>.
                                </p>
                            </section>

                            <section className="grid gap-5 md:grid-cols-2">
                                <button
                                    onClick={() => {
                                        setPhase('reading');
                                        setActiveQuestion(0);
                                        setQuizResults([]);
                                    }}
                                    className="flex min-h-[150px] flex-col justify-between border border-[#8cacff] bg-[#171d2e] px-7 py-6 text-left transition-colors hover:bg-[#202845]"
                                >
                                    <span className="font-['IBM_Plex_Mono'] text-xs uppercase tracking-[0.28em] text-[#8cacff]">
                                        Retry Route
                                    </span>
                                    <span className="font-['Space_Grotesk'] text-2xl font-black uppercase italic text-white">
                                        Re-open Briefing
                                    </span>
                                </button>
                                <Link
                                    to={`/courses/${courseId}`}
                                    className="flex min-h-[150px] flex-col justify-between border border-[#2a2a2a] bg-[#161616] px-7 py-6 transition-colors hover:bg-[#1d1d1d]"
                                >
                                    <span className="font-['IBM_Plex_Mono'] text-xs uppercase tracking-[0.28em] text-[#767575]">
                                        Abort Path
                                    </span>
                                    <span className="font-['Space_Grotesk'] text-2xl font-black uppercase italic text-white">
                                        Back To Quest Map
                                    </span>
                                </Link>
                            </section>
                        </motion.div>
                    )}
                </AnimatePresence>
            </div>

            <LoginModal
                isOpen={showLoginModal}
                onClose={() => {
                    setShowLoginModal(false);
                    setLoginContext(null);
                }}
                onContinueAsGuest={handleGuestProceed}
            />
        </div>
    );
}
